/**
 * Storage for the backend JWT (and the expiry timestamp that rides with it).
 *
 * Why this exists as a module instead of six inline `localStorage.getItem('token')`
 * calls: in a WKWebView, localStorage is an unencrypted SQLite file inside the app
 * container. Any future "protect the session with Face ID" story is pure theater
 * unless the token itself moves into the Keychain — and moving it means every read
 * becomes asynchronous. So the API is async NOW, while the only backend is still
 * synchronous localStorage, and the native swap becomes a one-file change with zero
 * call-site churn.
 *
 * DO NOT reach for @capacitor/preferences for the native backend. Preferences is
 * NSUserDefaults, i.e. a plist in the app container — exactly the same at-rest
 * exposure as localStorage, just with a nicer API. It is a convenience store for
 * settings, not secure storage. The native backend has to be a real Keychain plugin
 * (kSecAttrAccessibleAfterFirstUnlockThisDeviceOnly or stricter).
 *
 * The key names are deliberately unchanged ('token' / 'tokenExpiration') so the web
 * app keeps every already-signed-in session across this refactor, and so the reads
 * that still live outside this module (assistantService.ts) stay consistent with it.
 */

const TOKEN_KEY = 'token';
const EXPIRATION_KEY = 'tokenExpiration';

export interface TokenStoreBackend {
  /** Async read — the only shape a Keychain-backed backend can honestly implement. */
  get(key: string): Promise<string | null>;
  /**
   * Best-effort SYNCHRONOUS read, used in exactly one place: seeding React state on
   * the first render so the web app doesn't paint a logged-out frame it will
   * immediately take back. A backend that cannot answer synchronously returns null
   * and lets the async hydrate settle it.
   */
  peek(key: string): string | null;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

/**
 * localStorage backend. Note the writes land synchronously (nothing awaits before
 * touching storage) even though the signature is a promise — callers must not rely
 * on that, but it does mean this refactor changes no observable web timing.
 *
 * Every call is wrapped: Safari in private mode throws QuotaExceededError on
 * setItem, and losing the token is strictly better than crashing the app.
 */
const localStorageBackend: TokenStoreBackend = {
  async get(key) {
    return localStorageBackend.peek(key);
  },
  peek(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  async set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn(`Failed to persist ${key}:`, error);
    }
  },
  async remove(key) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn(`Failed to clear ${key}:`, error);
    }
  },
};

let backend: TokenStoreBackend = localStorageBackend;

/**
 * Install a different backend (the native Keychain one). Call this before the app
 * renders — AuthProvider reads the store on mount.
 */
export const setTokenStoreBackend = (next: TokenStoreBackend): void => {
  backend = next;
};

/** Restore the default localStorage backend. Exported for tests. */
export const resetTokenStoreBackend = (): void => {
  backend = localStorageBackend;
};

export const getToken = (): Promise<string | null> => backend.get(TOKEN_KEY);

/** Synchronous best-effort read. See TokenStoreBackend.peek — may return null on native. */
export const peekToken = (): string | null => backend.peek(TOKEN_KEY);

/** Epoch milliseconds at which the stored JWT stops being accepted, or null. */
export const getTokenExpiration = async (): Promise<number | null> => {
  const raw = await backend.get(EXPIRATION_KEY);
  if (!raw) return null;
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

export const setToken = async (token: string, expiresAtMs: number): Promise<void> => {
  await backend.set(TOKEN_KEY, token);
  await backend.set(EXPIRATION_KEY, String(expiresAtMs));
};

export const clearToken = async (): Promise<void> => {
  await backend.remove(TOKEN_KEY);
  await backend.remove(EXPIRATION_KEY);
};
